/*
 * ReviewsStrip — public social-proof widget (Iter 76)
 * ===================================================
 * Compact horizontal strip showing aggregate rating + up to 3 recent approved
 * reviews. Renders NOTHING when there are no approved reviews to avoid an
 * empty section. Used on HomePage and AcademyLandingPage.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Star, Quote } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReviewsStrip = ({ scope, title = "Τι λένε για εμάς" }) => {
  const [stats, setStats] = useState(null);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    const q = scope ? `?scope=${scope}` : "";
    Promise.all([
      axios.get(`${API}/reviews/stats${q}`),
      axios.get(`${API}/reviews${q}${q ? "&" : "?"}limit=3`),
    ])
      .then(([s, r]) => {
        setStats(s.data);
        setReviews(r.data || []);
      })
      .catch(() => {});
  }, [scope]);

  if (!stats || stats.count === 0) return null;

  return (
    <section
      className="py-14 sm:py-16 px-6 border-y border-[#1e1e1e] bg-gradient-to-b from-black to-[#080808]"
      data-testid="reviews-strip"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-[#F5A623] text-xs uppercase tracking-widest mb-1">
              Social Proof
            </p>
            <h2 className="font-['Bebas_Neue'] text-3xl md:text-4xl text-white tracking-tight">
              {title}
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex gap-0.5">
              {[1, 2, 3, 4, 5].map((n) => (
                <Star
                  key={n}
                  size={18}
                  className={
                    n <= Math.round(stats.avg_rating)
                      ? "fill-[#F5A623] text-[#F5A623]"
                      : "text-zinc-700"
                  }
                />
              ))}
            </div>
            <div className="text-sm">
              <span className="text-white font-semibold">{stats.avg_rating}</span>
              <span className="text-zinc-500"> / 5 · </span>
              <span className="text-zinc-400">{stats.count} κριτικές</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {reviews.map((r) => (
            <blockquote
              key={r.id}
              className="relative bg-[#0a0a0a] border border-[#1e1e1e] rounded-lg p-5 hover:border-[#F5A623]/30 transition-colors"
              data-testid={`review-card-${r.id}`}
            >
              <Quote
                size={22}
                className="absolute top-4 right-4 text-[#F5A623]/20"
              />
              <div className="flex gap-0.5 mb-2">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star
                    key={n}
                    size={12}
                    className={
                      n <= r.rating
                        ? "fill-[#F5A623] text-[#F5A623]"
                        : "text-zinc-700"
                    }
                  />
                ))}
              </div>
              {r.title && (
                <p className="text-white font-semibold text-sm mb-1">
                  {r.title}
                </p>
              )}
              <p className="text-zinc-400 text-sm leading-relaxed line-clamp-4">
                {r.body}
              </p>
              <footer className="mt-3 pt-3 border-t border-[#1e1e1e]">
                <p className="text-xs text-zinc-500">
                  <span className="text-zinc-300">{r.author_name}</span>
                  {r.author_role && (
                    <span> · {r.author_role === "parent" ? "Γονέας" :
                                 r.author_role === "player" ? "Παίκτης" :
                                 r.author_role === "supporter" ? "Οπαδός" : ""}</span>
                  )}
                </p>
              </footer>
            </blockquote>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ReviewsStrip;
